<script setup lang="ts">
import { computed } from 'vue';
import { THEME } from '@pkg/config/misc/theme';
import { buttonRecipe } from '@pkg/ui/components/ui/button/buttonRecipe';
import type { AllButton } from '@pkg/sanity-utils/types/button';
import { ButtonGroup } from '@pkg/modular-blocks/blocks/inner/ButtonGroup';
import CustomLink from '@pkg/link/src/components/CustomLink.vue';
import type { LinkType } from '@pkg/link';
import type { NewHeaderAction, NewHeaderActionItem } from '../types/mainNav';
import { resolveHeaderActions } from '../composables/useHeaderActionsResolution';
import LanguagePicker from './LanguagePicker.vue';

const props = defineProps<{
  navActions: null | NewHeaderAction[];
  mobileHeaderActionKey?: string;
  textColor?: string;
  variant?: 'footer';
  rootClass?: string;
  contentTheme?: 'light' | 'dark';
  linkClass?: string;
  buttonGroupClass?: string;
}>();

const allActions = computed(() => props.navActions ?? []);
const mobileActions = computed(() =>
  props.mobileHeaderActionKey
    ? allActions.value.filter(a => a._key === props.mobileHeaderActionKey)
    : [],
);

const allResolved = computed(() => resolveHeaderActions(allActions.value));
const mobileResolved = computed(() => resolveHeaderActions(mobileActions.value));
const footerResolved = computed(() =>
  props.variant === 'footer' ? resolveHeaderActions(allActions.value) : { links: [] as LinkType[], buttons: [] as AllButton[] },
);

const showMobile = computed(() => props.variant === 'footer' && props.rootClass && (footerResolved.value.links.length > 0 || footerResolved.value.buttons.length > 0));

const hasHeaderActions = computed(() => !props.variant && allActions.value.length > 0);
const showMobileSlot = computed(() => !props.variant && mobileActions.value.length > 0);

const headerSlots = computed(() => {
  const mobileRes = mobileResolved.value;
  const mobileCtaLink =
    showMobileSlot.value && mobileRes.links.length === 1 && !mobileRes.buttons.length
      ? mobileRes.links[0]
      : null;
  return [
    { id: 'desktop' as const, show: hasHeaderActions.value, resolved: allResolved.value, listClass: 'c-nav__actions-list c-nav__actions-list--desktop hidden desk:flex', withLanguagePicker: true },
    { id: 'tablet' as const, show: hasHeaderActions.value, resolved: allResolved.value, listClass: 'c-nav__actions-list c-nav--tablet hidden tablet-wide:flex desk:!hidden', withLanguagePicker: false },
    { id: 'mobile' as const, show: showMobileSlot.value, resolved: mobileRes, listClass: 'c-nav__actions-list c-nav__actions-list--mobile flex tablet-wide:!hidden', withLanguagePicker: false, mobileCtaLink },
  ].filter(s => s.show);
});

function itemTitle(item: NewHeaderActionItem | LinkType): string {
  return item && 'title' in item ? item.title : '';
}

function linkKey(link: LinkType, index: number, prefix: string): string {
  return (link as { _key?: string })?._key ?? `${prefix}-${index}`;
}

const buttonConfig = computed(() => ({
  size: 'medium' as const,
  recipeVariant: (props.contentTheme === 'light' ? 'DarkMode' : 'Primary') as 'DarkMode' | 'Primary',
}));
const headerTheme = computed(() => (props.contentTheme === 'light' ? THEME.LIGHT : props.contentTheme === 'dark' ? THEME.DARK : undefined));

const mobileLinkAsCtaClass = computed(() => [
  buttonRecipe({ variant: buttonConfig.value.recipeVariant, size: buttonConfig.value.size }),
  'c-button-group-link',
]);

</script>

<template>
  <div>
    <div
      v-if="showMobile"
      :class="[rootClass, contentTheme && `${rootClass}--${contentTheme}`]"
      aria-label="Additional actions"
    >
      <CustomLink
        v-for="(link, index) in footerResolved.links"
        :key="linkKey(link, index, 'footer')"
        :link="link"
        :show-separator="index < footerResolved.links.length - 1 || footerResolved.buttons.length > 0"
        :class="linkClass"
      >
        {{ itemTitle(link) }}
      </CustomLink>
      <ButtonGroup
        v-if="footerResolved.buttons.length > 0"
        :buttons="footerResolved.buttons"
        :config="buttonConfig"
        :theme="headerTheme"
        :class="buttonGroupClass"
      />
    </div>
    <template v-for="slot in headerSlots" :key="slot.id">
      <div :class="slot.listClass">
        <div class="c-nav__links-wrapper">
          <template v-if="slot.mobileCtaLink">
            <span :data-theme="headerTheme" class="c-nav__mobile-cta-wrapper">
              <CustomLink
                :key="linkKey(slot.mobileCtaLink, 0, slot.id)"
                :link="slot.mobileCtaLink"
                :class="mobileLinkAsCtaClass"
              >
                {{ itemTitle(slot.mobileCtaLink) }}
              </CustomLink>
            </span>
          </template>
          <template v-else>
            <CustomLink
              v-for="(link, index) in slot.resolved.links"
              :key="linkKey(link, index, slot.id)"
              :link="link"
              :show-separator="index < slot.resolved.links.length - 1"
              class="c-nav__links-item"
            >
              {{ itemTitle(link) }}
            </CustomLink>
            <ButtonGroup
              v-if="slot.resolved.buttons.length > 0"
              :buttons="slot.resolved.buttons"
              :config="buttonConfig"
              :theme="headerTheme"
              class="c-nav__buttons"
            />
          </template>
        </div>
        <LanguagePicker
          v-if="slot.withLanguagePicker"
          :content-side-offset="6"
          content-class="py-12"
        />
      </div>
    </template>
  </div>
</template>

<style scoped>
.c-nav__actions-list {
  align-items: center;

  &--mobile {
    justify-content: center;
    gap: 1rem;
  }

  &--desktop {
    @screen desk {
      display: flex;
      width: 100%;
      gap: 1rem;
      justify-content: flex-end;
    }
  }

  &:deep(.c-nav__links-item) {
    &:hover {
      text-decoration: underline;
    }
  }
}

.c-nav__links-wrapper {
  display: flex;
  align-items: center;
  gap: 1rem;

  @screen tablet-wide {
    flex: 0 0 auto;
  }
}

.c-nav__mobile-cta-wrapper {
  display: inline-flex;
}

.c-nav--tablet {
  flex: 1;
  justify-content: flex-end;
  gap: 1rem;
}
</style>
